import { confirmEmailCode, sendEmailChallenge } from '@client/new/api/auth.api';
import {
  RegisterInput,
  RegistrationResponse,
  register,
} from '@client/new/api/user.api';
import { keys } from '@client/new/helpers/general';
import { createDispatch } from '@client/new/helpers/types-utils';
import { customerIOIdentify } from '@client/new/services/customerio';
import { useUserStore } from '@client/new/stores/user.store';
import { captureAttribution } from '@services/attribution-cookies';
import { initHubspot, trackSubmission } from '@services/hubspot/HubspotService';
import { useMemo } from 'react';
import {
  clearEmailChallengeForEmail,
  markEmailChallengeSent,
  shouldSendEmailChallenge,
} from './auth.challenge.model';
import { toCompleteRegistrationError } from './auth.registration-error.model';
import { resolveDefaultPhoneCountryDialCode } from './auth.phone-options.model';
import {
  buildRegistrationPhoneNumber as buildPhone,
  REGISTRATION_EMAIL_REGEX,
  isRegistrationEmail,
  REGISTRATION_PHONE_NUMBER_REGEX,
  normalizeRegistrationEmail as normalizeEmail,
  normalizeRegistrationInstagram as normalizeInstagram,
  normalizeRegistrationString as trim,
  normalizeRegistrationTiktok as normalizeTiktok,
  normalizeRegistrationYoutube as normalizeYoutube,
  toUnregisteredUser,
} from './registration.model';
import {
  clearFlowSession,
  isTimestampExpired,
  persistFlowSession,
  readFlowSession,
} from './registration.draft.model';
import type {
  AuthIdentityPolicy,
  AuthModel,
  AuthModelActionResultMap,
  AuthModelActionsMap,
  AuthModelQueries,
  CompleteRegistrationInput,
  Email,
  InstagramHandle,
  Phone,
  RegistrationCodeResult,
  RegistrationDraft,
  SessionUser,
  TiktokHandle,
  YoutubeChannelInput,
} from './auth.types';

const REGISTRATION_DRAFT_STORAGE_KEY = 'new-registration-draft';

type UnknownRecord = Record<string, unknown>;

type SendChallengeOptions = NonNullable<
  AuthModelActionsMap['SendRegistrationChallenge']['options']
>;

type HubspotRegistrationLeadInput =
  AuthModelActionsMap['SubmitHubspotRegistrationLead'];

type ChallengePayload = AuthModelActionsMap['SendRegistrationChallenge'];

const REGISTRATION_DRAFT_DEFAULTS: RegistrationDraft = {
  email: '',
  firstName: '',
  lastName: '',
  instagram: '',
  tiktok: '',
  youtube: '',
  phoneCountryDialCode: resolveDefaultPhoneCountryDialCode(),
  phoneNumber: '',
};

export const REGISTRATION_DRAFT_KEYS = keys(REGISTRATION_DRAFT_DEFAULTS);

const createEmptyRegistrationDraft = (): RegistrationDraft => ({
  ...REGISTRATION_DRAFT_DEFAULTS,
});

const HUBSPOT_REGISTRATION_FORM = {
  formId: 'b1a307d8-c205-4b61-962c-80ddc54a9e03',
  portalId: '45090276',
} as const;

const isProductionEnvironment = (): boolean =>
  import.meta.env.VITE_ENV === 'production';
const toCustomerIOPhone = (phone: Phone): string => phone.replace(/\s+/g, '');

const isRecord = (value: unknown): value is UnknownRecord => {
  return !!value && typeof value === 'object';
};

const isSessionUser = (value: unknown): value is SessionUser => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === 'number' &&
    typeof value.email === 'string' &&
    typeof value.token === 'string' &&
    value.token.length > 0
  );
};

const readDraftFromStorage = (): RegistrationDraft => {
  const emptyRegistrationDraft = createEmptyRegistrationDraft();

  try {
    const serialized = localStorage.getItem(REGISTRATION_DRAFT_STORAGE_KEY);
    if (!serialized) {
      return emptyRegistrationDraft;
    }

    const parsed = JSON.parse(serialized) as unknown;
    if (!isRecord(parsed)) {
      return emptyRegistrationDraft;
    }

    const updatedAt =
      typeof parsed.updatedAt === 'number' ? parsed.updatedAt : null;
    if (updatedAt === null || isTimestampExpired(updatedAt)) {
      clearDraftFromStorage();
      return emptyRegistrationDraft;
    }

    return {
      email: typeof parsed.email === 'string' ? parsed.email : '',
      firstName: typeof parsed.firstName === 'string' ? parsed.firstName : '',
      lastName: typeof parsed.lastName === 'string' ? parsed.lastName : '',
      instagram: typeof parsed.instagram === 'string' ? parsed.instagram : '',
      tiktok: typeof parsed.tiktok === 'string' ? parsed.tiktok : '',
      youtube: typeof parsed.youtube === 'string' ? parsed.youtube : '',
      phoneCountryDialCode:
        typeof parsed.phoneCountryDialCode === 'string' &&
        parsed.phoneCountryDialCode.trim().length > 0
          ? parsed.phoneCountryDialCode
          : emptyRegistrationDraft.phoneCountryDialCode,
      phoneNumber:
        typeof parsed.phoneNumber === 'string' ? parsed.phoneNumber : '',
    };
  } catch {
    return emptyRegistrationDraft;
  }
};

const writeDraftToStorage = (draft: RegistrationDraft): void => {
  try {
    localStorage.setItem(
      REGISTRATION_DRAFT_STORAGE_KEY,
      JSON.stringify({
        ...draft,
        updatedAt: Date.now(),
      }),
    );
  } catch {
    return;
  }
};

const clearDraftFromStorage = (): void => {
  try {
    localStorage.removeItem(REGISTRATION_DRAFT_STORAGE_KEY);
  } catch {
    return;
  }
};

const parseEmail = (emailInput: string): Email | null => {
  const email = normalizeEmail(emailInput);
  return isRegistrationEmail(email) ? (email as Email) : null;
};

const parseInstagram = (instagramInput: string): InstagramHandle | null => {
  const instagramHandle = normalizeInstagram(instagramInput);
  return instagramHandle.length > 0
    ? (instagramHandle as InstagramHandle)
    : null;
};

const parseTiktok = (tiktokInput: string): TiktokHandle | null => {
  const tiktokHandle = normalizeTiktok(tiktokInput);
  return tiktokHandle.length > 0 ? (tiktokHandle as TiktokHandle) : null;
};

const parseYoutubeChannel = (
  youtubeInput: string,
): YoutubeChannelInput | null => {
  const youtubeChannel = normalizeYoutube(youtubeInput);
  return youtubeChannel.length > 0
    ? (youtubeChannel as YoutubeChannelInput)
    : null;
};

const parsePhone = (
  dialCodeInput: string,
  numberInput: string,
): Phone | null => {
  const phone = buildPhone(dialCodeInput, numberInput);
  return phone ? (phone as Phone) : null;
};

const toEmailValue = (emailValue: string): Email | null => {
  return REGISTRATION_EMAIL_REGEX.test(emailValue)
    ? (emailValue as Email)
    : null;
};

const toInstagramHandleValue = (
  instagramHandleValue: string,
): InstagramHandle | null => {
  return instagramHandleValue.length > 0
    ? (instagramHandleValue as InstagramHandle)
    : null;
};

const toTiktokHandleValue = (
  tiktokHandleValue: string,
): TiktokHandle | null => {
  return tiktokHandleValue.length > 0
    ? (tiktokHandleValue as TiktokHandle)
    : null;
};

const toYoutubeChannelValue = (
  youtubeChannelValue: string,
): YoutubeChannelInput | null => {
  return youtubeChannelValue.length > 0
    ? (youtubeChannelValue as YoutubeChannelInput)
    : null;
};

const toPhoneValue = (phoneValue: string | null): Phone | null => {
  if (!phoneValue) {
    return null;
  }

  return REGISTRATION_PHONE_NUMBER_REGEX.test(phoneValue)
    ? (phoneValue as Phone)
    : null;
};

const mapToRegisterInput = (
  input: CompleteRegistrationInput,
): RegisterInput => {
  const email = toEmailValue(input.user.email);
  if (!email) {
    throw new Error('Invalid registration payload');
  }

  const instagramHandle = toInstagramHandleValue(input.user.instagram);
  const tiktokHandle = toTiktokHandleValue(input.user.tiktok);
  const youtubeChannel = toYoutubeChannelValue(input.user.youtube);
  const phone = toPhoneValue(input.user.phone);

  return {
    token: trim(input.token),
    email,
    firstName: input.user.firstName,
    lastName: input.user.lastName,
    salesModel: 'subscription',
    ...(instagramHandle ? { profileInstagram: instagramHandle } : {}),
    ...(tiktokHandle ? { profileTiktok: tiktokHandle } : {}),
    ...(youtubeChannel ? { profileYoutubeChannel: youtubeChannel } : {}),
    ...(phone ? { mobilePhoneNumber: phone } : {}),
  };
};

const identifyCustomerWithOptionalPhone = (
  user: Pick<SessionUser, 'influencerId' | 'firstName' | 'lastName'>,
  email: string,
  phone: Phone | null,
): void => {
  if (!phone) {
    return;
  }

  customerIOIdentify({
    id: email,
    ...(user.influencerId ? { influencerId: user.influencerId } : {}),
    email,
    first_name: user.firstName ?? undefined,
    last_name: user.lastName ?? undefined,
    phone_number: toCustomerIOPhone(phone),
  });
};

const createAuthModel = (): AuthModel => {
  const loadDraft = (): RegistrationDraft => {
    return readDraftFromStorage();
  };

  const readRegisterFlowSession = () => {
    return readFlowSession();
  };

  const persistDraftField = (
    payload: AuthModelActionsMap['PersistDraftField'],
  ): RegistrationDraft => {
    const nextDraft = {
      ...loadDraft(),
      [payload.key]: payload.value,
    };
    writeDraftToStorage(nextDraft);
    return nextDraft;
  };

  const loginUser = (user: SessionUser): null => {
    useUserStore.getState().login(user);
    return null;
  };

  const initRegistrationAttribution = (): void => {
    captureAttribution();
    initHubspot();
  };

  const submitHubspotRegistrationLead = async ({
    email,
    firstName,
    lastName,
    instagramHandle,
  }: HubspotRegistrationLeadInput): Promise<void> => {
    if (!isProductionEnvironment()) {
      return;
    }

    const parsedEmail = parseEmail(email);
    if (!parsedEmail) {
      return;
    }

    const parsedInstagramHandle = parseInstagram(instagramHandle);

    await trackSubmission({
      data: {
        email: parsedEmail,
        instagram_handle: parsedInstagramHandle ?? '',
        firstname: trim(firstName),
        lastname: trim(lastName),
      },
      ...HUBSPOT_REGISTRATION_FORM,
    });
  };

  const identity: AuthIdentityPolicy = {
    parseEmail,
    isEmail: isRegistrationEmail,
    parseInstagram,
    parseTiktok,
    parseYoutubeChannel,
    parsePhone,
  };

  const queries: AuthModelQueries = {
    loadDraft,
    readFlowSession: readRegisterFlowSession,
    identity,
  };

  const sendChallenge = async (
    email: Email,
    options?: SendChallengeOptions,
  ): Promise<void> => {
    const mode = options?.mode ?? 'if-needed';

    if (mode !== 'force' && !shouldSendEmailChallenge(email)) {
      return;
    }

    await sendEmailChallenge(email, options?.intent);
    markEmailChallengeSent(email);
  };

  const sendRegistrationChallenge = async ({
    email,
    options,
  }: ChallengePayload): Promise<void> => {
    const parsedEmail = identity.parseEmail(email);
    if (!parsedEmail) {
      return;
    }

    await sendChallenge(parsedEmail, {
      ...options,
      intent: options?.intent ?? 'signup',
    });
  };

  const sendSignInChallenge = async ({
    email,
    options,
  }: ChallengePayload): Promise<void> => {
    const parsedEmail = identity.parseEmail(email);
    if (!parsedEmail) {
      return;
    }

    await sendChallenge(parsedEmail, {
      ...options,
      intent: options?.intent ?? 'login',
    });
  };

  const submitRegistrationCode = async ({
    email,
    code,
  }: AuthModelActionsMap['SubmitRegistrationCode']): Promise<RegistrationCodeResult> => {
    const parsedEmail = identity.parseEmail(email);
    if (!parsedEmail) {
      throw new Error('Invalid email challenge payload');
    }

    const response = await confirmEmailCode(parsedEmail, trim(code));

    if (isSessionUser(response.user)) {
      return { type: 'existing-user', value: response.user };
    }

    const token = trim(response.token ?? '');
    if (token.length > 0) {
      return {
        type: 'new-user',
        value: { token },
      };
    }

    throw new Error('Invalid confirmation response payload');
  };

  const completeRegistration = async ({
    token,
    values,
  }: AuthModelActionsMap['CompleteRegistration']): Promise<RegistrationResponse> => {
    try {
      const user = toUnregisteredUser(values);
      const payload = mapToRegisterInput({
        token,
        user,
      });
      const registeredUser = await register(payload);
      identifyCustomerWithOptionalPhone(
        registeredUser,
        payload.email,
        toPhoneValue(user.phone),
      );
      return registeredUser;
    } catch (error) {
      throw toCompleteRegistrationError(error);
    }
  };

  const dispatch = createDispatch<
    AuthModelActionsMap,
    AuthModelActionResultMap
  >({
    PersistDraftField: (payload) => persistDraftField(payload),
    ClearDraft: () => {
      clearDraftFromStorage();
      return null;
    },
    PersistFlowSession: (payload) => {
      persistFlowSession(payload);
      return null;
    },
    ClearFlowSession: () => {
      clearFlowSession();
      return null;
    },
    ClearEmailChallenge: ({ email }) => {
      const parsedEmail = identity.parseEmail(email);
      if (parsedEmail) {
        clearEmailChallengeForEmail(parsedEmail);
      }
      return null;
    },
    InitRegistrationAttribution: () => {
      initRegistrationAttribution();
      return null;
    },
    LoginUser: (payload) => loginUser(payload),
    SubmitHubspotRegistrationLead: (payload) =>
      submitHubspotRegistrationLead(payload),
    SendRegistrationChallenge: (payload) => sendRegistrationChallenge(payload),
    SendSignInChallenge: (payload) => sendSignInChallenge(payload),
    SubmitRegistrationCode: (payload) => submitRegistrationCode(payload),
    CompleteRegistration: (payload) => completeRegistration(payload),
  });

  return {
    queries,
    dispatch,
  };
};

export const useAuthModel = (): AuthModel => {
  return useMemo(() => createAuthModel(), []);
};
